import { Group, Stack, Box } from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import RevisionStatusIcon from 'components/RevisionStatusIcon';
import P1Medium from 'components/typography/P1Medium';
import RevisionList from 'components/RevisionList';
import { useCallback } from 'react';
import openBrainItUrl from 'utils/open-brain-it-url';
import { useSignRevisionModal } from 'components/modals/sign-revision-modal/SignRevisionModalProvider';
import { useFinishSigningModal } from 'components/modals/finish-signing-modal/FinishSigningModalProvider';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import Button from 'components/Button';

export interface KepGroup {
  kepMessageUuid: string | undefined;
  revisions: Array<{
    revisionId: number;
    revisionName: string;
    kepMessageUuid?: string;
    kind: string;
  }>;
}

interface KepGroupCardProps {
  kepGroup: KepGroup;
}

/**
 * Component that displays a group of revisions with the same kepMessageUuid.
 */
export default function KepGroupCard({ kepGroup: { kepMessageUuid, revisions } }: KepGroupCardProps) {
  const { signRevisionModal } = useSignRevisionModal();
  const { finishSigningModal } = useFinishSigningModal();
  const { getAction } = useApi();
  const isRevisionKind = revisions[0]?.kind === 'revision';

  /** Handle signing a group with defined kepMessageUuid (redirect to NFQES). */
  const openExistingSignLink = useCallback(() => {
    if (!kepMessageUuid) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      panic(new Error('No kepMessageUuid found for this group'));
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    const message =
      revisions.length === 1
        ? 'Podpisová správa už bola odoslaná do systému BrainIT. Pre dokončenie podpisovania budete presmerovaný do ich webového portálu.'
        : 'Podpisové správy už boli odoslané do systému BrainIT. Pre dokončenie podpisovania budete presmerovaný do ich webového portálu.';

    finishSigningModal({ kepMessageUuid, message });
  }, [kepMessageUuid, revisions.length, finishSigningModal]);

  /** Handle signing a group without kepMessageUuid (open SignRevisionModal). */
  const createAndOpenNewSignLink = useCallback(() => {
    const revisionIds = revisions.map(({ revisionId }) => revisionId);

    const message =
      revisions.length === 1
        ? isRevisionKind
          ? 'Revízna správa bude odoslaná do systému BrainIT. Pre jej podpísanie budete presmerovaný do ich webového portálu.'
          : 'Formulár bude odoslaný do systému BrainIT. Pre jeho podpísanie budete presmerovaný do ich webového portálu.'
        : isRevisionKind
        ? 'Revízne správy budú odoslané do systému BrainIT. Pre ich podpísanie budete presmerovaný do ich webového portálu.'
        : 'Formuláre budú odoslané do systému BrainIT. Pre ich podpísanie budete presmerovaný do ich webového portálu.';

    signRevisionModal({
      revisionIds,
      message,
      onSign: async ({ deadline }) => {
        const revisionSignMultipleDocsAction = getAction('RevisionSignMultipleDocs');

        try {
          const { redirectUrl } = await revisionSignMultipleDocsAction({
            payload: {
              revisionIds,
              documentValidUntil: deadline,
            },
          });

          openBrainItUrl(redirectUrl);
        } catch (e: any) {
          panic(e);
        }
      },
    });
  }, [revisions, signRevisionModal, getAction, isRevisionKind]);

  const onSignClick = kepMessageUuid ? openExistingSignLink : createAndOpenNewSignLink;

  if (revisions.length === 0) {
    return null;
  }

  return (
    <Box p={12} pb={8} style={{ border: '1px solid #E9ECEF', borderRadius: '8px', backgroundColor: '#F8F9FA' }}>
      <Group position="apart" align="flex-start" noWrap>
        <Stack spacing={8} style={{ flex: 1 }}>
          <RevisionStatusIcon statusId={kepMessageUuid ? 9 : 8} TextComponent={P1Medium} kind={revisions[0]?.kind} />
          <RevisionList revisions={revisions} unstyled />
        </Stack>
        <Group spacing={8} noWrap>
          <Button variant="secondary" size="sm" leftIcon={<IconSignature />} onClick={onSignClick}>
            Podpísať ({revisions.length})
          </Button>
        </Group>
      </Group>
    </Box>
  );
}
